import { Link } from "@tanstack/react-router";
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  Eye,
  Lock,
  MoreHorizontal,
  Share2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { evaluarElegibilidad } from "@/lib/licitacion-elegibilidad";
import { mercadoPublicoUrl, shareTender } from "@/lib/licitaciones";
import { licitacionesService } from "@/services/licitaciones.service";
import type { LicitacionListItem, TenantTracking } from "@/types";

export function LicitacionActionsMenu({
  licitacion,
  onPreview,
  onTrackingChange,
}: {
  licitacion: LicitacionListItem;
  onPreview: () => void;
  onTrackingChange?: (tracking: TenantTracking) => void;
}) {
  const elegibilidad = evaluarElegibilidad(licitacion);
  const mutate = async (kind: "track" | "postulada") => {
    try {
      let tracking = licitacion.tracking;
      if (!tracking) tracking = await licitacionesService.track(licitacion.id);
      if (kind === "postulada")
        tracking = await licitacionesService.updateTracking(licitacion.id, {
          estadoPipeline: "postulada",
        });
      onTrackingChange?.(tracking);
      toast.success(
        kind === "track"
          ? "Licitación incorporada al pipeline"
          : "Licitación marcada como postulada",
      );
    } catch {
      toast.error("No fue posible completar la acción");
    }
  };
  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copiado`);
    } catch {
      toast.error("No fue posible copiar");
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Acciones de ${licitacion.codigo}`}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuItem onSelect={onPreview}>
          <Eye /> Vista rápida
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/licitaciones/$id" params={{ id: licitacion.id }}>
            <ExternalLink /> Ver detalle completo
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={mercadoPublicoUrl(licitacion.codigo)} target="_blank" rel="noreferrer">
            <ExternalLink /> Ficha oficial
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {!elegibilidad.postulable && (
          <DropdownMenuItem disabled className="text-xs whitespace-normal">
            <Lock /> {elegibilidad.motivo}
          </DropdownMenuItem>
        )}
        {!licitacion.tracking && (
          <DropdownMenuItem onSelect={() => mutate("track")}>
            <CheckCircle2 /> {elegibilidad.postulable ? "Incorporar al pipeline" : "Seguir (solo consulta)"}
          </DropdownMenuItem>
        )}
        {elegibilidad.postulable && licitacion.tracking?.estadoPipeline !== "postulada" && (
          <DropdownMenuItem onSelect={() => mutate("postulada")}>
            <CheckCircle2 /> Marcar como postulada
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => copy(licitacion.codigo, "Código")}>
          <Copy /> Copiar código
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => copy(mercadoPublicoUrl(licitacion.codigo), "Enlace")}>
          <Copy /> Copiar enlace oficial
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={async () => {
            try {
              const result = await shareTender(licitacion);
              toast.success(result === "shared" ? "Licitación compartida" : "Enlace copiado");
            } catch {
              toast.error("No fue posible compartir");
            }
          }}
        >
          <Share2 /> Compartir
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
